import React, { useState, useEffect } from 'react';
import Modal, { ModalTitle, ModalActions, ModalActionsSecondary } from '../Modal/Modal';
import { PortalField } from '../FormField/PortalField';
import { PortalInput } from '../FormField/PortalInput';
import Button from '../Button/Button';
import ScheduleStep from '../ScheduleStep/ScheduleStep';
import { combineDateTime, scheduleValidationError, type ScheduledFor } from '../../lib/scheduling';
import styles from './TransferToAdopterModal.module.scss';

export interface AdopterTransferDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface TransferToAdopterModalProps {
  open: boolean;
  onClose: () => void;
  /** Device or module identifier shown in the modal description. */
  deviceId: string;
  /** Optional pet name appended in parentheses after the device id. */
  petName?: string | null;
  /** When set, a banner notes the chip will be transferred to the adopter automatically. */
  chipSerialNumber?: string | null;
  /** Whether to show the rescue subscription discount banner. */
  showRescueDiscount?: boolean;
  /**
   * Pre-fill values, synced into the form each time the modal opens. Used when
   * re-entering the transfer after "I entered the wrong adopter info" so the
   * rescue can correct the existing details instead of retyping them.
   */
  initialFirstName?: string | null;
  initialLastName?: string | null;
  initialEmail?: string | null;
  initialPhone?: string | null;
  /** The pet is in a foster home, and will be recalled on the way through. */
  recallFirst?: boolean;
  onTransfer: (firstName: string, lastName: string, email: string, phone: string) => void;
  onSchedule?: (details: AdopterTransferDetails, when: ScheduledFor) => Promise<void>;
}

export default function TransferToAdopterModal({
  open,
  onClose,
  deviceId,
  petName,
  chipSerialNumber,
  showRescueDiscount,
  initialFirstName,
  initialLastName,
  initialEmail,
  initialPhone,
  recallFirst,
  onTransfer,
  onSchedule,
}: TransferToAdopterModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Seed the form from the pre-fill values whenever the modal opens. Keyed on
  // `open` so re-opening for a different device re-applies its details (the
  // component instance persists between opens, so state would otherwise linger).
  useEffect(() => {
    if (open) {
      setFirstName(initialFirstName ?? '');
      setLastName(initialLastName ?? '');
      setEmail(initialEmail ?? '');
      setPhone(initialPhone ?? '');
      setScheduling(false);
      setDate('');
      setTime('');
      setSubmitError(null);
    }
  }, [open, initialFirstName, initialLastName, initialEmail, initialPhone]);

  function handleClose() {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setScheduling(false);
    setDate('');
    setTime('');
    setSubmitError(null);
    onClose();
  }

  const canSubmit = !!firstName.trim() && !!email.trim() && !!phone.trim();
  const details: AdopterTransferDetails = {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    email: email.trim(),
    phone: phone.trim(),
  };

  function handleConfirm() {
    if (!canSubmit) {
      return;
    }
    onTransfer(details.firstName, details.lastName, details.email, details.phone);
    handleClose();
  }

  const scheduleError = scheduleValidationError(date, time);
  const when = combineDateTime(date, time);
  const canConfirmSchedule = canSubmit && !!when && !scheduleError && !submitting;

  async function handleConfirmSchedule() {
    if (!canConfirmSchedule || !when || !onSchedule) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSchedule(details, when);
      handleClose();
    } catch (err) {
      // Kept open on failure so the rescue can adjust the time rather than lose the form.
      setSubmitError(err instanceof Error ? err.message : 'Could not schedule the adoption.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={handleClose} dismissOnBackdropClick={!scheduling}>
      <ModalTitle>{scheduling ? 'Schedule adoption' : 'Transfer to adopter'}</ModalTitle>

      {scheduling ? (
        <ScheduleStep
          actionLabel="The transfer to the adopter"
          date={date}
          time={time}
          onDateChange={setDate}
          onTimeChange={setTime}
          error={scheduleError ?? submitError}
        >
          <p className={styles.modalDescription}>
            Pick when <strong>{deviceId}</strong>
            {petName ? ` (${petName})` : ''} should transfer to <strong>{details.firstName || 'the adopter'}</strong>.
          </p>
        </ScheduleStep>
      ) : (
        <>
          <p className={styles.modalDescription}>
            Transfer <strong>{deviceId}</strong>
            {petName ? ` (${petName})` : ''} to the adopter. The adopter will receive an email to set up their own Fi
            subscription.
          </p>

          {recallFirst && (
            <div className={`${styles.infoBanner} ${styles.recallBanner}`}>
              {petName || 'This pet'} will be recalled from their current foster first.
            </div>
          )}

          <PortalField label="Adopter name *">
            <div className={styles.fieldRow}>
              <PortalInput value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
              <PortalInput value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
            </div>
          </PortalField>

          <PortalField label="Adopter email *">
            <PortalInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. sarah@gmail.com"
            />
          </PortalField>

          <PortalField label="Adopter phone *">
            <PortalInput
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. (555) 555-0100"
            />
          </PortalField>

          {showRescueDiscount && (
            <div className={styles.infoBanner}>
              The adopter will receive a special rescue discount on their Fi subscription.
            </div>
          )}

          {chipSerialNumber && (
            <div className={styles.infoBanner}>
              This pet&apos;s microchip (<strong>{chipSerialNumber}</strong>) will also be transferred to the adopter
              automatically.
            </div>
          )}
        </>
      )}

      <ModalActions>
        {scheduling ? (
          <>
            <Button variant="ghost" onClick={() => setScheduling(false)} disabled={submitting}>
              Back
            </Button>
            <Button variant="green" onClick={handleConfirmSchedule} disabled={!canConfirmSchedule}>
              {submitting ? 'Scheduling…' : 'Schedule transfer'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="green" onClick={handleConfirm} disabled={!canSubmit}>
              Transfer to Adopter Now
            </Button>
          </>
        )}
      </ModalActions>

      {!scheduling && onSchedule && (
        <ModalActionsSecondary>
          <Button variant="link" onClick={() => setScheduling(true)} disabled={!canSubmit}>
            Schedule for later
          </Button>
        </ModalActionsSecondary>
      )}
    </Modal>
  );
}
